import React, { useMemo, useState, useCallback } from "react";
import {
	ScatterChart,
	Scatter,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
	ReferenceArea,
} from "recharts";
import "./style.css";

// --- SVG Icon Shapes ---
const createSvgIcon = (pathD, color, size = 18) => (props) => {
	const { cx, cy } = props;
	if (!cx || !cy) return null;
	const half = size / 2;
	return (
		<g transform={`translate(${cx - half}, ${cy - half})`}>
			<circle cx={half} cy={half} r={half} fill={color} opacity={0.15} />
			<svg viewBox="0 0 24 24" width={size} height={size} fill={color}>
				<path d={pathD} />
			</svg>
		</g>
	);
};

// Material Design icon paths
const ICONS = {
	bedIn: createSvgIcon(
		"M7 13c1.66 0 3-1.34 3-3S8.66 7 7 7s-3 1.34-3 3 1.34 3 3 3zm12-6h-8v7H3V5H1v15h2v-3h18v3h2v-9c0-2.21-1.79-4-4-4z",
		"#22c55e", 18
	),
	bedOut: createSvgIcon(
		"M13.5 5.5c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zM9.8 8.9L7 23h2.1l1.8-8 2.1 2v6h2v-7.5l-2.1-2 .6-3C14.8 12 16.8 13 19 13v-2c-1.9 0-3.5-1-4.3-2.4l-1-1.6c-.4-.6-1-1-1.7-1-.3 0-.5.1-.8.1L6 8.3V13h2V9.6l1.8-.7",
		"#f59e0b", 18
	),
	heartHigh: createSvgIcon(
		"M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z",
		"#ef4444", 18
	),
	heartLow: createSvgIcon(
		"M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z",
		"#ec4899", 18
	),
	lungHigh: createSvgIcon(
		"M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z",
		"#06b6d4", 18
	),
	lungLow: createSvgIcon(
		"M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z",
		"#3b82f6", 18
	),
	posture: createSvgIcon(
		"M12 6V9L16 5L12 1V4C7.58 4 4 7.58 4 12C4 13.57 4.46 15.03 5.24 16.26L6.7 14.8C6.25 13.97 6 13.01 6 12C6 8.69 8.69 6 12 6ZM18.76 7.74L17.3 9.2C17.74 10.04 18 10.99 18 12C18 15.31 15.31 18 12 18V15L8 19L12 23V20C16.42 20 20 16.42 20 12C20 10.43 19.54 8.97 18.76 7.74Z",
		"#8b5cf6", 18
	),
	postureDetected: createSvgIcon(
		"M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z",
		"#22c55e", 18
	),
};

// --- Event Config ---
const EVENT_CONFIG = {
	inBed: { color: "#22c55e", label: "Yatağa Girdi", shape: ICONS.bedIn, yValue: 7 },
	outOfBed: { color: "#f59e0b", label: "Yataktan Çıktı", shape: ICONS.bedOut, yValue: 6 },
	"High Heart Rate": { color: "#ef4444", label: "Yüksek Kalp Atışı", shape: ICONS.heartHigh, yValue: 5 },
	"Low Heart Rate": { color: "#ec4899", label: "Düşük Kalp Atışı", shape: ICONS.heartLow, yValue: 4 },
	"High Respiration Rate": { color: "#06b6d4", label: "Yüksek Solunum", shape: ICONS.lungHigh, yValue: 3 },
	"Low Respiration Rate": { color: "#3b82f6", label: "Düşük Solunum", shape: ICONS.lungLow, yValue: 2 },
	"Posture Change": { color: "#8b5cf6", label: "Pozisyon Değişimi", shape: ICONS.posture, yValue: 1 },
	"Posture Change Detected": { color: "#22c55e", label: "Pozisyon Tamamlandı", shape: ICONS.postureDetected, yValue: 1 },
	"In Bed": { color: "#22c55e", label: "Yatağa Girdi", shape: ICONS.bedIn, yValue: 7 },
	"Out Of Bed": { color: "#f59e0b", label: "Yataktan Çıktı", shape: ICONS.bedOut, yValue: 6 },
};

const Y_LABELS = {
	1: "Pozisyon Değişimi",
	2: "Düşük Solunum",
	3: "Yüksek Solunum",
	4: "Düşük Kalp Atışı",
	5: "Yüksek Kalp Atışı",
	6: "Yataktan Çıktı",
	7: "Yatağa Girdi",
};

const FALLBACK_CONFIG = { color: "#94a3b8", label: "Bilinmeyen", shape: ICONS.posture, yValue: 0 };

// --- Helpers ---
const formatDetails = (details) => {
	if (!details) return "";
	if (details.toLowerCase().includes("above")) {
		const m = details.match(/(\d+)\s*(bpm|rpm)/i);
		return m ? `${m[1]} ${m[2].toLowerCase()}'nin üzerinde` : details;
	}
	if (details.toLowerCase().includes("below")) {
		const m = details.match(/(\d+)\s*(bpm|rpm)/i);
		return m ? `${m[1]} ${m[2].toLowerCase()}'in altında` : details;
	}
	return details;
};

const getEventType = (item) => {
	if (item.occupancy === "inBed") return "inBed";
	if (item.occupancy === "outOfBed" || item.title === "Out Of Bed") return "outOfBed";
	if (item.title === "High Heart Rate") return "High Heart Rate";
	if (item.title === "Low Heart Rate") return "Low Heart Rate";
	if (item.title === "High Respiration Rate") return "High Respiration Rate";
	if (item.title === "Low Respiration Rate") return "Low Respiration Rate";
	if (!item.occupancy && item.title === "Posture Change") {
		return item.details === "Detected" ? "Posture Change Detected" : "Posture Change";
	}
	return item.occupancy || item.title || "unknown";
};

const getEventLabel = (item) => {
	const type = getEventType(item);
	const cfg = EVENT_CONFIG[type];
	if (!cfg) return item.title || "Bilinmeyen";

	if (type === "High Heart Rate" || type === "Low Heart Rate") {
		return `${cfg.label}${item.details ? ` (${formatDetails(item.details)})` : ""}`;
	}
	if (type === "High Respiration Rate" || type === "Low Respiration Rate") {
		return `${cfg.label}${item.details ? ` (${formatDetails(item.details)})` : ""}`;
	}
	if (type === "Posture Change") {
		if (item.details?.includes("prevent pressure ulcers")) {
			return "Bası yaralarını önlemek için pozisyon verin";
		}
		return item.details || "Pozisyon Değişimi";
	}
	return cfg.label;
};

// --- Component ---
const EventChart = ({ data }) => {
	const [refAreaLeft, setRefAreaLeft] = useState(null);
	const [refAreaRight, setRefAreaRight] = useState(null);
	const [xDomain, setXDomain] = useState(null);
	const [isZooming, setIsZooming] = useState(false);
	const [zoomLevel, setZoomLevel] = useState(1);

	const chartData = useMemo(() => {
		if (!data || !Array.isArray(data) || data.length === 0) return [];

		return data
			.filter((item) => {
				if (!item.time) return false;
				return (
					item.occupancy === "inBed" ||
					item.occupancy === "outOfBed" ||
					item.title === "Out Of Bed" ||
					item.title === "High Heart Rate" ||
					item.title === "Low Heart Rate" ||
					item.title === "High Respiration Rate" ||
					item.title === "Low Respiration Rate" ||
					item.title === "Posture Change"
				);
			})
			.map((item) => {
				const eventType = getEventType(item);
				const cfg = EVENT_CONFIG[eventType] || FALLBACK_CONFIG;
				const date = new Date(item.time);
				return {
					time: date.getTime(),
					timeLabel: date.toLocaleString("tr-TR", {
						year: "numeric",
						month: "long",
						day: "numeric",
						hour: "2-digit",
						minute: "2-digit",
						second: "2-digit",
					}),
					label: getEventLabel(item),
					value: cfg.yValue,
					color: cfg.color,
					type: eventType,
				};
			})
			.sort((a, b) => a.time - b.time);
	}, [data]);

	const groupedData = useMemo(() => {
		const groups = {};
		chartData.forEach((item) => {
			if (!groups[item.type]) groups[item.type] = [];
			groups[item.type].push(item);
		});
		return groups;
	}, [chartData]);

	const xAxisPadding = useMemo(() => {
		if (chartData.length < 2) return 0;
		const times = chartData.map((d) => d.time);
		return (Math.max(...times) - Math.min(...times)) * 0.03;
	}, [chartData]);

	// --- Zoom handlers ---
	const handleMouseDown = useCallback((e) => {
		if (e?.activeLabel) {
			setRefAreaLeft(e.activeLabel);
			setIsZooming(true);
		}
	}, []);

	const handleMouseMove = useCallback((e) => {
		if (isZooming && e?.activeLabel) setRefAreaRight(e.activeLabel);
	}, [isZooming]);

	const handleMouseUp = useCallback(() => {
		if (refAreaLeft && refAreaRight) {
			const left = Math.min(refAreaLeft, refAreaRight);
			const right = Math.max(refAreaLeft, refAreaRight);
			const filtered = chartData.filter((d) => d.time >= left && d.time <= right);
			if (filtered.length > 0) {
				setXDomain([left, right]);
			}
		}
		setRefAreaLeft(null);
		setRefAreaRight(null);
		setIsZooming(false);
	}, [refAreaLeft, refAreaRight, chartData]);

	const resetZoom = useCallback(() => {
		setXDomain(null);
		setZoomLevel(1);
	}, []);

	const zoom = useCallback((direction) => {
		if (chartData.length === 0) return;
		const times = chartData.map((d) => d.time);
		const dataMin = Math.min(...times);
		const dataMax = Math.max(...times);
		const dataRange = dataMax - dataMin;

		const newLevel = direction === "in"
			? Math.min(zoomLevel * 1.5, 10)
			: Math.max(zoomLevel / 1.5, 1);
		setZoomLevel(newLevel);

		if (newLevel <= 1) { setXDomain(null); return; }

		const zoomRange = dataRange / newLevel;
		const center = xDomain ? (xDomain[0] + xDomain[1]) / 2 : (dataMin + dataMax) / 2;
		let newMin = center - zoomRange / 2;
		let newMax = center + zoomRange / 2;
		if (newMin < dataMin) { newMin = dataMin; newMax = dataMin + zoomRange; }
		if (newMax > dataMax) { newMax = dataMax; newMin = dataMax - zoomRange; }
		setXDomain([newMin, newMax]);
	}, [xDomain, chartData, zoomLevel]);

	const pan = useCallback((direction) => {
		if (!xDomain || chartData.length === 0) return;
		const times = chartData.map((d) => d.time);
		const dataMin = Math.min(...times);
		const dataMax = Math.max(...times);
		const range = xDomain[1] - xDomain[0];
		const amount = range * 0.3;

		if (direction === "left") {
			const newMin = Math.max(dataMin, xDomain[0] - amount);
			setXDomain([newMin, newMin + range]);
		} else {
			const newMax = Math.min(dataMax, xDomain[1] + amount);
			setXDomain([newMax - range, newMax]);
		}
	}, [xDomain, chartData]);

	// Aktif event tiplerinin legend'ını oluştur
	const activeLegend = useMemo(() => {
		const seen = new Set();
		return chartData
			.map((d) => {
				if (seen.has(d.type)) return null;
				seen.add(d.type);
				const cfg = EVENT_CONFIG[d.type];
				if (!cfg) return null;
				return { key: d.type, color: cfg.color, label: cfg.label };
			})
			.filter(Boolean)
			.sort((a, b) => {
				const aY = EVENT_CONFIG[a.key]?.yValue || 0;
				const bY = EVENT_CONFIG[b.key]?.yValue || 0;
				return bY - aY;
			});
	}, [chartData]);

	// --- Custom Components ---
	const CustomTooltip = ({ active, payload }) => {
		if (!active || !payload?.length) return null;
		const d = payload[0].payload;
		return (
			<div className="ec-tooltip" style={{ borderLeftColor: d.color }}>
				<div className="ec-tooltip-label" style={{ color: d.color }}>{d.label}</div>
				<div className="ec-tooltip-time">{d.timeLabel}</div>
			</div>
		);
	};

	const CustomYTick = ({ x, y, payload }) => {
		const label = Y_LABELS[payload.value] || "";
		return (
			<text x={x - 8} y={y} textAnchor="end" dominantBaseline="central" fontSize={12} fill="#64748b">
				{label}
			</text>
		);
	};

	if (!chartData || chartData.length === 0) {
		return (
			<div className="ec-empty">
				<div className="ec-empty-icon">
					<svg viewBox="0 0 24 24" width="48" height="48" fill="#cbd5e1">
						<path d="M11 15h2v2h-2zm0-8h2v6h-2zm.99-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z" />
					</svg>
				</div>
				<p className="ec-empty-text">Gösterilecek alarm verisi bulunamadı.</p>
				<p className="ec-empty-hint">Lütfen tarih aralığı seçin.</p>
			</div>
		);
	}

	const formatXAxis = (tick) => {
		return new Date(tick).toLocaleString("tr-TR", {
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const zoomText = zoomLevel <= 1 ? "100%" : `${Math.round(zoomLevel * 100)}%`;
	const isZoomed = xDomain !== null;

	return (
		<div className="ec-wrapper">
			{/* Toolbar */}
			<div className="ec-toolbar">
				<button className="ec-btn ec-btn-nav" onClick={() => pan("left")} disabled={!isZoomed} title="Sola Kaydır">
					<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" /></svg>
				</button>
				<button className="ec-btn ec-btn-zoom" onClick={() => zoom("out")} disabled={zoomLevel <= 1} title="Uzaklaştır">
					<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M19 13H5v-2h14v2z" /></svg>
				</button>
				<div className={`ec-zoom-badge ${isZoomed ? "active" : ""}`}>{zoomText}</div>
				<button className="ec-btn ec-btn-zoom" onClick={() => zoom("in")} disabled={zoomLevel >= 10} title="Yakınlaştır">
					<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" /></svg>
				</button>
				<button className="ec-btn ec-btn-nav" onClick={() => pan("right")} disabled={!isZoomed} title="Sağa Kaydır">
					<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" /></svg>
				</button>
				<button className="ec-btn ec-btn-reset" onClick={resetZoom} disabled={!isZoomed} title="Sıfırla">
					<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M12 6V9L16 5L12 1V4C7.58 4 4 7.58 4 12C4 13.57 4.46 15.03 5.24 16.26L6.7 14.8C6.25 13.97 6 13.01 6 12C6 8.69 8.69 6 12 6ZM18.76 7.74L17.3 9.2C17.74 10.04 18 10.99 18 12C18 15.31 15.31 18 12 18V15L8 19L12 23V20C16.42 20 20 16.42 20 12C20 10.43 19.54 8.97 18.76 7.74Z" /></svg>
					Sıfırla
				</button>
			</div>

			{/* Chart */}
			<div className="ec-chart-container">
				<ResponsiveContainer width="100%" height={380}>
					<ScatterChart
						margin={{ top: 20, right: 30, bottom: 20, left: 130 }}
						onMouseDown={handleMouseDown}
						onMouseMove={handleMouseMove}
						onMouseUp={handleMouseUp}
						onMouseLeave={() => {
							if (isZooming) {
								setRefAreaLeft(null);
								setRefAreaRight(null);
								setIsZooming(false);
							}
						}}
					>
						<CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
						<XAxis
							type="number"
							dataKey="time"
							domain={xDomain || [
								(dataMin) => dataMin - xAxisPadding,
								(dataMax) => dataMax + xAxisPadding,
							]}
							tickFormatter={formatXAxis}
							stroke="#94a3b8"
							fontSize={11}
							tickLine={false}
							axisLine={{ stroke: "#e2e8f0" }}
							allowDataOverflow
						/>
						<YAxis
							type="number"
							dataKey="value"
							domain={[0.5, 7.5]}
							ticks={[1, 2, 3, 4, 5, 6, 7]}
							tick={<CustomYTick />}
							stroke="#94a3b8"
							tickLine={false}
							axisLine={{ stroke: "#e2e8f0" }}
							allowDecimals={false}
							allowDataOverflow
							width={120}
						/>
						<Tooltip
							content={<CustomTooltip />}
							cursor={{ strokeDasharray: "3 3", stroke: "#94a3b8" }}
							wrapperStyle={{ zIndex: 1000 }}
							isAnimationActive={false}
						/>
						{Object.keys(groupedData).map((type) => {
							const cfg = EVENT_CONFIG[type] || FALLBACK_CONFIG;
							const visibleData = xDomain
								? groupedData[type].filter((d) => d.time >= xDomain[0] && d.time <= xDomain[1])
								: groupedData[type];

							return (
								<Scatter
									key={type}
									name={cfg.label}
									data={visibleData}
									fill={cfg.color}
									shape={cfg.shape}
									isAnimationActive={false}
								/>
							);
						})}
						{refAreaLeft && refAreaRight && (
							<ReferenceArea
								x1={refAreaLeft}
								x2={refAreaRight}
								strokeOpacity={0.3}
								fill="#3b82f6"
								fillOpacity={0.1}
							/>
						)}
					</ScatterChart>
				</ResponsiveContainer>
			</div>

			{/* Legend */}
			<div className="ec-legend">
				{activeLegend.map((item) => (
					<div key={item.key} className="ec-legend-item">
						<span className="ec-legend-dot" style={{ backgroundColor: item.color }} />
						<span className="ec-legend-label">{item.label}</span>
					</div>
				))}
			</div>
		</div>
	);
};

export default EventChart;
